// =============================================================================
// ExceptionForm - Form riusabile per assenze / presenze aggiuntive
// =============================================================================
// Usato sia dal pannello della singola risorsa (resourceId fisso) sia dalla
// sezione centralizzata "Assenze" (con selettore risorsa).

import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Loader2, CalendarOff, CalendarPlus, X, Check, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDateLocale } from '../../hooks/useDateLocale';
import { DatePickerInput } from '../ui/DatePickerInput';
import { Toggle } from '../ui/Toggle';
import { ResourceCombobox } from '../ui/ResourceCombobox';
import { ReasonCombobox } from '../ui/ReasonCombobox';
import {
  useCreateResourceException,
  useUpdateResourceException,
  useExceptionConflicts,
  useAbsenceReasons,
  ResourceException,
  ExceptionInput,
  ExceptionConflictQuery,
} from '../../api/hooks';
import { Resource } from '../../types';

interface ExceptionFormProps {
  /** Risorsa fissa (es. dal pannello della singola risorsa). */
  resourceId?: string;
  /** Se fornito (e in creazione), mostra un selettore di risorsa. */
  resources?: Resource[];
  /** Eccezione esistente (modalità modifica). */
  exception: ResourceException | null;
  onClose: () => void;
}

export function ExceptionForm({ resourceId, resources, exception, onClose }: ExceptionFormProps) {
  const { t } = useTranslation('settings');
  const createException = useCreateResourceException();
  const updateException = useUpdateResourceException();
  const { data: reasons = [] } = useAbsenceReasons(true);
  const isEditing = !!exception;

  const today = format(new Date(), 'yyyy-MM-dd');

  // In modifica la risorsa è fissa; in creazione si usa il selettore se disponibile.
  const showResourcePicker = !isEditing && !!resources && !resourceId;
  const [selectedResourceId, setSelectedResourceId] = useState(
    exception?.resourceId ?? resourceId ?? '',
  );

  const [isAvailable, setIsAvailable] = useState(exception?.isAvailable ?? false);
  const [reasonId, setReasonId] = useState<string>(exception?.reasonId ?? '');
  const [isPeriod, setIsPeriod] = useState(
    exception ? exception.startDate.slice(0, 10) !== exception.endDate.slice(0, 10) : false,
  );
  const [startDate, setStartDate] = useState(exception?.startDate.slice(0, 10) ?? today);
  const [endDate, setEndDate] = useState(exception?.endDate.slice(0, 10) ?? today);
  const [specificHours, setSpecificHours] = useState(!!exception?.startTime);
  const [startTime, setStartTime] = useState(exception?.startTime ?? '09:00');
  const [endTime, setEndTime] = useState(exception?.endTime ?? '13:00');
  const [title, setTitle] = useState(exception?.title ?? '');
  const [notes, setNotes] = useState(exception?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const locale = useDateLocale();

  const isSaving = createException.isPending || updateException.isPending;

  // --- Rilevamento appuntamenti in conflitto con l'assenza ---
  const targetResourceId = isEditing ? exception!.resourceId : selectedResourceId;
  const effectiveEndDate = isPeriod ? endDate : startDate;
  const conflictQuery: ExceptionConflictQuery | null =
    !isAvailable && !!startDate && effectiveEndDate >= startDate
      ? {
          startDate,
          endDate: effectiveEndDate,
          startTime: specificHours ? startTime : undefined,
          endTime: specificHours ? endTime : undefined,
        }
      : null;
  const { data: conflicts, isFetching: checkingConflicts } = useExceptionConflicts(
    targetResourceId,
    conflictQuery,
    !isAvailable && !!targetResourceId,
  );

  const handleSubmit = async () => {
    setError(null);

    const targetResourceId = isEditing ? exception!.resourceId : selectedResourceId;
    if (!targetResourceId) {
      setError(t('exceptions.errors.resourceRequired'));
      return;
    }

    if (!isAvailable && !reasonId) {
      setError(t('exceptions.errors.reasonRequired'));
      return;
    }

    const effectiveEndDate = isPeriod ? endDate : startDate;
    if (effectiveEndDate < startDate) {
      setError(t('exceptions.errors.endBeforeStart'));
      return;
    }
    if (specificHours && startTime >= endTime) {
      setError(t('exceptions.errors.timeOrder'));
      return;
    }

    const payload: ExceptionInput = {
      reasonId: isAvailable ? null : reasonId,
      startDate,
      endDate: effectiveEndDate,
      startTime: specificHours ? startTime : null,
      endTime: specificHours ? endTime : null,
      title: title.trim() || null,
      notes: notes.trim() || null,
      isAvailable,
    };

    try {
      if (isEditing && exception) {
        await updateException.mutateAsync({
          resourceId: exception.resourceId,
          exceptionId: exception.id,
          ...payload,
        });
      } else {
        await createException.mutateAsync({ resourceId: targetResourceId, ...payload });
      }
      onClose();
    } catch (e) {
      console.error('Error saving exception:', e);
      setError(t('exceptions.errors.saveFailed'));
    }
  };

  return (
    <div className="space-y-5">
      <h4 className="text-base font-semibold text-gray-900">
        {isEditing ? t('exceptions.editTitle') : t('exceptions.createTitle')}
      </h4>

      {/* Selettore risorsa (solo in modalità centralizzata) */}
      {showResourcePicker && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('exceptions.resourceLabel')}
          </label>
          <ResourceCombobox
            resources={resources!}
            value={selectedResourceId}
            onChange={setSelectedResourceId}
            placeholder={t('exceptions.resourcePlaceholder')}
            noMatchLabel={t('exceptions.resourceNoMatch')}
          />
        </div>
      )}

      {/* Tipo: Assenza vs Presenza aggiuntiva */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setIsAvailable(false)}
          className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
            !isAvailable
              ? 'border-red-300 bg-red-50 text-red-700'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'
          }`}
        >
          <CalendarOff className="w-4 h-4" />
          {t('exceptions.absence')}
        </button>
        <button
          type="button"
          onClick={() => setIsAvailable(true)}
          className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
            isAvailable
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'
          }`}
        >
          <CalendarPlus className="w-4 h-4" />
          {t('exceptions.presence')}
        </button>
      </div>

      {/* Causale (solo per assenze) */}
      {!isAvailable && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('exceptions.reasonLabel')}
          </label>
          {reasons.length === 0 ? (
            <p className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg px-3 py-2.5">
              {t('exceptions.reasonEmpty')}
            </p>
          ) : (
            <ReasonCombobox
              reasons={reasons}
              value={reasonId}
              onChange={setReasonId}
              placeholder={t('exceptions.reasonPlaceholder')}
              noMatchLabel={t('exceptions.reasonNoMatch')}
            />
          )}
        </div>
      )}

      {/* Titolo (opzionale) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('exceptions.titleLabel')}{' '}
          <span className="text-gray-400 font-normal">({t('exceptions.optional')})</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('exceptions.titlePlaceholder')}
          className="w-full px-3 py-2 input"
        />
      </div>

      {/* Periodo vs giorno singolo */}
      <div className="flex items-center gap-3">
        <Toggle checked={isPeriod} onChange={() => setIsPeriod((v) => !v)} />
        <span className="text-sm text-gray-700">{t('exceptions.periodToggle')}</span>
      </div>

      <div className={`grid gap-3 ${isPeriod ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {isPeriod ? t('exceptions.startDate') : t('exceptions.date')}
          </label>
          <DatePickerInput value={startDate} onChange={setStartDate} />
        </div>
        {isPeriod && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('exceptions.endDate')}
            </label>
            <DatePickerInput value={endDate} onChange={setEndDate} />
          </div>
        )}
      </div>

      {/* Orari specifici vs giornata intera */}
      <div className="flex items-center gap-3">
        <Toggle checked={specificHours} onChange={() => setSpecificHours((v) => !v)} />
        <span className="text-sm text-gray-700">{t('exceptions.specificHours')}</span>
      </div>

      {specificHours && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('schedule.from')}
            </label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full px-3 py-2 input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('schedule.to')}
            </label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full px-3 py-2 input"
            />
          </div>
        </div>
      )}

      {/* Note */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('exceptions.notesLabel')}
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder={t('exceptions.notesPlaceholder')}
          className="w-full px-3 py-2 input"
        />
      </div>

      {/* Avviso appuntamenti in conflitto con l'assenza */}
      {!isAvailable && conflicts && conflicts.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-amber-800">
                {t('exceptions.conflictsTitle', { count: conflicts.length })}
              </p>
              <p className="text-xs text-amber-700 mt-0.5">{t('exceptions.conflictsHint')}</p>
              <ul className="mt-2 space-y-1">
                {conflicts.slice(0, 5).map((c) => (
                  <li key={c.allocationId} className="text-xs text-amber-800 flex items-center gap-1.5">
                    <span className="font-medium">
                      {format(parseISO(c.startTime), 'd MMM HH:mm', { locale })}
                      {' – '}
                      {format(parseISO(c.endTime), 'HH:mm', { locale })}
                    </span>
                    <span className="text-amber-600">·</span>
                    <span className="truncate">
                      {c.serviceName || c.title}
                      {c.customerName ? ` — ${c.customerName}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
              {conflicts.length > 5 && (
                <p className="text-xs text-amber-700 mt-1">
                  {t('exceptions.conflictsMore', { count: conflicts.length - 5 })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {!isAvailable && checkingConflicts && (!conflicts || conflicts.length === 0) && (
        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" />
          {t('exceptions.conflictsChecking')}
        </p>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Azioni */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <X className="w-4 h-4" />
          {t('common:buttons.cancel')}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSaving}
          className="btn-primary flex items-center gap-2 px-4 py-2 text-sm"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {isEditing ? t('common:buttons.saveChanges') : t('exceptions.add')}
        </button>
      </div>
    </div>
  );
}
